import React from "react";
import * as Request from "superagent";
import Cookie from "js-cookie";
import CustomEvent from "custom-event";
import uniqId from "uniqid";
import { SuburbModel } from "../../models/SuburbModel";
import { ApiBasePath } from "../../utils/ApiBasePath";
import { setConfiguratorPostcode } from "../../utils/dxp-postcode";

interface State {
  isLoading: boolean;
  isValid: boolean;
  input: string;
  suburbs: SuburbModel[];
  error: string;
  preSelectedSuburb: SuburbModel; //Selected in dropdown but not actually "Selected" (saved)
  selectedSuburb: SuburbModel; //Actual selected (saved)
  isRestored: boolean;
  lookupTime: number;
}

interface Props {
  id?: string;
  onSelected?: Function;
  onPreSelect?: Function;
  onInputChange?: Function;
  saveOnPreSelect?: boolean;
  placeholder?: string;
  inputShowsSelected?: boolean;
  fieldClassName?: string;
  doNotRestore?: boolean;
  simpleDisplay?: boolean;
}

class PostcodeField extends React.PureComponent<Props, State> {
  private input: any = null;
  private postcodeCache: SuburbModel[] = []; //Local cache
  private syncId = uniqId();

  constructor(props: Props) {
    super(props);

    let restoredSuburb: SuburbModel = null;
    let isRestored: boolean = false;

    if (props.doNotRestore !== true) {
      const savedSuburb: string = localStorage.getItem("suburb");

      if (
        typeof savedSuburb !== "undefined" &&
        savedSuburb &&
        savedSuburb !== "null"
      ) {
        restoredSuburb = JSON.parse(savedSuburb);
        isRestored = true;
      }
    }

    this.state = {
      isLoading: false,
      isValid: props.inputShowsSelected ? isRestored : false,
      suburbs: [],
      error: null,
      input: isRestored ? this.formatFullSuburbInput(restoredSuburb) : "",
      isRestored,
      preSelectedSuburb: restoredSuburb,
      selectedSuburb: null,
      lookupTime: null,
    };
  }

  log(message: string): void {
    document.location.href.indexOf("?log") > -1 &&
      console.log(`PostcodeField(${this.syncId}):${message}`);
  }

  componentDidMount(): void {
    if (this.state.isRestored) {
      const suburb = this.state.preSelectedSuburb;
      this.selectSuburb(suburb, true);
      this.log(`Restored location (${suburb.postcode})`);
    }

    window.addEventListener("syncPostcodeSave", (e: any) => {
      const suburb = e.detail.suburb;
      const emitterSyncId = e.detail.syncId;
      if (emitterSyncId === this.syncId) {
        return;
      }
      this.log(`Sync Apply (from:${emitterSyncId})`);
      this.selectSuburb(suburb, this.state.isRestored, true);
    });
  }

  public setFocus(): void {
    this.input.focus();
  }

  public formSubmit(): void {
    this.log(`FormSubmit`);
    const { suburbs, preSelectedSuburb } = this.state;

    if (preSelectedSuburb) {
      return this.selectSuburb();
    }

    if (this.state.suburbs && this.state.suburbs.length > 0) {
      this.preSelectSuburb(suburbs[0]);
    }
  }

  private cleanInput(str: string = this.state.input): string {
    return str.replace(/\D/g, "");
  }

  private formatFullSuburbInput(suburb: SuburbModel): string {
    return `${suburb.name}, ${suburb.postcode} ${suburb.state}`;
  }

  private formatSuburb(suburb: SuburbModel): string {
    const simpleDisplay = this.props.simpleDisplay || false;
    return simpleDisplay
      ? `${suburb.postcode}`
      : this.formatFullSuburbInput(suburb);
  }

  private handleInputChange(value): void {
    const postcode: number = parseInt(value);
    const validPostcode: boolean = !(
      isNaN(postcode) ||
      postcode < 100 ||
      postcode > 9999
    );
    const now = new Date();
    const newValue = value.substring(0, 4);

    if (newValue === this.state.input) {
      return;
    }

    this.props.onInputChange && this.props.onInputChange(newValue);

    this.setState(
      {
        input: newValue,
        suburbs: [],
        preSelectedSuburb: null,
        error: null,
        isLoading: validPostcode,
        isValid: false,
        lookupTime: now.getMilliseconds(),
      },
      () => {
        if (validPostcode) {
          this.getSuburbs(value, now);
        }
      }
    );
  }

  private toTitleCase(str: string): string {
    return str.replace(
      /\w\S*/g,
      (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
    );
  }

  private getSuburbs(postcode: number, when: Date): void {
    const url: string = `${ApiBasePath.location}?search=${this.state.input}`;
    const cacheKey: string = `_${postcode.toString()}`;

    // Restore from local cache if any
    if (typeof this.postcodeCache[cacheKey] !== "undefined") {
      return this.setState({
        isLoading: false,
        suburbs: this.postcodeCache[cacheKey],
      });
    }

    Request.get(url).then(
      (response) => {
        const rawSuburbs = response.body;
        const suburbs = rawSuburbs.length
          ? rawSuburbs.map((res) => {
              return {
                postcode: res.postcode,
                name: this.toTitleCase(res.suburbName),
                state: res.stateAbbreviationName,
              };
            })
          : [];

        suburbs.sort((a, b) => {
          if (a.name < b.name) return -1;
          if (a.name > b.name) return 1;
          return 0;
        });

        this.postcodeCache[cacheKey] = suburbs;

        if (this.state.lookupTime === when.getMilliseconds()) {
          this.setState({
            isLoading: false,
            suburbs,
            lookupTime: null,
          });
        }
      },
      (error) => {
        if (this.state.lookupTime === when.getMilliseconds()) {
          this.setState({
            isLoading: false,
            error: error,
          });
        }
      }
    );
  }

  private preSelectSuburb(suburb: SuburbModel): void {
    this.log(`Pre-Select (pick)`);
    this.setState(
      {
        preSelectedSuburb: suburb,
        isValid: true,
        suburbs: [],
        error: null,
        input: this.formatSuburb(suburb),
      },
      () => {
        this.props.onPreSelect && this.props.onPreSelect.call(this, suburb);
        this.props.saveOnPreSelect && this.selectSuburb();
      }
    );
  }

  private selectSuburb(
    preSelectedSuburb: SuburbModel = this.state.preSelectedSuburb,
    isRestored: boolean = false,
    isSynced: boolean = false
  ): void {
    this.log(`Select (save)`);

    const { onSelected } = this.props;

    if (!preSelectedSuburb) return;

    Cookie.get("suburb") && Cookie.remove("suburb");
    Cookie.set("suburb", JSON.stringify(preSelectedSuburb), { expires: 365 });

    localStorage.setItem("suburb", JSON.stringify(preSelectedSuburb));

    setConfiguratorPostcode(preSelectedSuburb.postcode);

    //Set state
    this.setState(
      {
        selectedSuburb: preSelectedSuburb,
        preSelectedSuburb,
        suburbs: [],
        error: null,
        input: this.props.inputShowsSelected
          ? this.formatSuburb(preSelectedSuburb)
          : "",
        isValid: true,
        isRestored,
      },
      () => {
        onSelected && onSelected.call(this, preSelectedSuburb, isRestored);

        if (!isSynced) {
          const evt: CustomEvent = new CustomEvent("syncPostcodeSave", {
            detail: { suburb: preSelectedSuburb, syncId: this.syncId },
          });
          this.log(`Sync Emit`);
          window.dispatchEvent(evt);
        }
      }
    );
  }

  render() {
    const {
      input,
      suburbs,
      preSelectedSuburb,
      isLoading,
      selectedSuburb,
      isValid,
    } = this.state;
    const { placeholder, id, fieldClassName } = this.props;

    return (
      <div
        className={`pc__fieldWrap`}
        onBlur={(e) => {
          setTimeout(() => {
            this.state.suburbs.length > 0 && this.setState({ suburbs: [] });
          }, 500);
        }}
      >
        <input
          id={id || "post-code"}
          type="text"
          className={`pc__input ${isLoading ? "pc__input--isLoading" : ""} ${
            isValid ? "pc__input--isValid" : ""
          } ${fieldClassName ? fieldClassName : ""} `}
          autoComplete="nope" //https://stackoverflow.com/questions/12374442/chrome-ignores-autocomplete-off
          placeholder={placeholder ? placeholder : ""}
          // maxLength={4}
          // min={100}
          // max={9999}
          value={input}
          onFocus={(e: any): void => {
            this.setState({ input: this.cleanInput(e.target.value) });
          }}
          onBlur={(e: any): void => {
            const suburb = this.state.preSelectedSuburb;
            if (!suburb) return;

            if (this.cleanInput(e.target.value) === suburb.postcode) {
              this.setState({
                input: this.formatSuburb(suburb),
              });
            }
          }}
          onChange={(e: any): void =>
            this.handleInputChange(this.cleanInput(e.target.value))
          }
          onKeyUp={(e: any): void => {
            if (e.target.keyCode === 13) {
              e.preventDefault();
              this.formSubmit();
            }
          }}
          ref={(input) => {
            this.input = input;
          }}
        />

        {suburbs && suburbs.length > 0 ? (
          <div
            className={`pc__results ${
              suburbs.length > 0 ? "pc__results--show" : ""
            }`}
          >
            {suburbs.map((suburb: SuburbModel) => {
              return (
                <div
                  className="pc__resultItem"
                  key={`suburb${suburb.name}`}
                  onClick={() => this.preSelectSuburb(suburb)}
                >
                  {suburb.name}
                </div>
              );
            })}
          </div>
        ) : null}
      </div>
    );
  }
}

export default PostcodeField;
